import React, { createContext, ReactNode, use, useEffect, useMemo, useState } from 'react';
import noop from 'lodash/noop';
import Loading from '@components-clean/core/loading/loading';
import { useGetCountriesForStudy } from '@apis/bff-api/central-monitoring';

type DropdownItem = {
    value: string | undefined;
    text: string;
};
const DefaultItem: DropdownItem = {
    value: undefined,
    text: 'No Country Filter',
};

export type CountrySelectorState = {
    country: DropdownItem;
    setCountry: (value: DropdownItem) => void;
    countryList: DropdownItem[];
    isLoading: boolean;
};
export const CountrySelectorContext = createContext<CountrySelectorState>({
    country: DefaultItem,
    setCountry: noop,
    countryList: [DefaultItem],
    isLoading: true,
});

interface Props {
    studyRef: string;
    children: ReactNode;
}

export const CountrySelectorContextProvider = ({ studyRef, children }: Props) => {
    const { data: countries, isLoading } = useGetCountriesForStudy(studyRef);

    const countryList: DropdownItem[] = countries
        ? [
              DefaultItem,
              ...countries
                  .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                  .map((c) => ({ value: c, text: c })),
          ]
        : [];

    const [country, setCountry] = useState<DropdownItem>(DefaultItem);
    // We'll use this state to track if we've successfully loaded countries/default selection (from search params) to
    // prevent extra data fetching
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);

    useEffect(() => {
        if (countries === undefined) return;
        let defaultSelection = DefaultItem;

        const query = new URLSearchParams(location.search);
        const countryQuery = query.get('country');
        if (countryQuery) defaultSelection = countryList.find((x) => x.value === countryQuery) || DefaultItem;

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setHasLoaded(true);
        setCountry(defaultSelection);
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [countries]);

    return (
        <CountrySelectorContext
            value={useMemo(
                () => ({
                    country,
                    setCountry,
                    countryList,
                    isLoading,
                }),
                // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                // eslint-disable-next-line @eslint-react/exhaustive-deps
                [studyRef, country, isLoading, countryList],
            )}
        >
            {/* If loading, avoid rendering children to prevent data fetches that might not be needed until the default country is selected */}
            {isLoading || !hasLoaded ? <Loading /> : children}
        </CountrySelectorContext>
    );
};

export const useCountrySelectorContext = () => use(CountrySelectorContext);
